import type {
  ExportJobSummary,
  MerchantStaffUser,
  RewardSummary,
  VendorMemberSummary,
  VendorOverview,
  VendorQrMaterials
} from "@moxipass/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

type Envelope<T> = { data: T; requestId: string };
type LoginResponse = {
  userId: string;
  actorType: string;
  role?: string;
  merchantId?: string;
  expiresAt: string;
  sessionToken: string;
  csrfToken: string;
};

const CSRF_STORAGE_KEY = "moxipass:csrf";
const SESSION_STORAGE_KEY = "moxipass:session";

function csrfToken() {
  const stored = sessionStorage.getItem(CSRF_STORAGE_KEY);
  if (stored) return stored;
  return document.cookie
    .split("; ")
    .find((entry) => entry.startsWith("mp_csrf="))
    ?.split("=")[1];
}

function isUnsafeMethod(method: string | undefined) {
  return !["GET", "HEAD", "OPTIONS"].includes((method ?? "GET").toUpperCase());
}

function authHeaders(init?: RequestInit, withContentType = true) {
  const headers = new Headers(init?.headers);
  if (withContentType) {
    headers.set("content-type", "application/json");
  }
  headers.set("x-actor-id", "usr_01jz0000000000000000000000");
  headers.set("x-actor-type", "vendor");
  headers.set("x-actor-role", "owner");
  headers.set("x-merchant-id", "mer_01jz0000000000000000000000");
  const sessionToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionToken) {
    headers.set("authorization", `Bearer ${sessionToken}`);
  }
  const csrf = csrfToken();
  if (isUnsafeMethod(init?.method) && csrf) {
    headers.set("x-csrf-token", decodeURIComponent(csrf));
  }
  return headers;
}

async function request<T>(path: string, init?: RequestInit) {
  const headers = authHeaders(init);
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers
  });
  const payload = (await response.json()) as Envelope<T> | { error: { message: string } };
  if (!response.ok || "error" in payload) {
    throw new Error("error" in payload ? payload.error.message : `Request failed: ${response.status}`);
  }
  return payload.data;
}

async function loginRequest(email: string, role = "owner", turnstileToken?: string) {
  const result = await request<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({
      email,
      actorType: "vendor",
      merchantId: "mer_01jz0000000000000000000000",
      role,
      turnstileToken
    })
  });
  sessionStorage.setItem(CSRF_STORAGE_KEY, result.csrfToken);
  sessionStorage.setItem(SESSION_STORAGE_KEY, result.sessionToken);
  return result;
}

async function uploadRequest<T>(path: string, body: FormData) {
  const init = { method: "POST", body };
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: authHeaders(init, false)
  });
  const payload = (await response.json()) as Envelope<T> | { error: { message: string } };
  if (!response.ok || "error" in payload) {
    throw new Error("error" in payload ? payload.error.message : `Request failed: ${response.status}`);
  }
  return payload.data;
}

function requireOnline(action: string) {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error(`Cannot ${action} while offline. Please reconnect and try again.`);
  }
}

export const api = {
  session: () =>
    request<{ userId: string; actorType: string; role?: string; merchantId?: string; expiresAt?: string }>("/v1/auth/session"),
  login: loginRequest,
  logout: async () => {
    const result = await request<{ loggedOut: boolean }>("/v1/auth/logout", { method: "POST", body: JSON.stringify({}) });
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return result;
  },
  overview: () => request<VendorOverview>("/v1/vendor/overview"),
  rewards: () => request<RewardSummary[]>("/v1/vendor/rewards"),
  members: () => request<{ members: VendorMemberSummary[] }>("/v1/vendor/members"),
  qrMaterials: () => request<VendorQrMaterials>("/v1/vendor/qr-materials"),
  createMerchant: (body: Record<string, unknown>) =>
    request<{
      merchantId: string;
      branchId: string;
      ownerId: string;
      programmeId: string;
      ruleId: string;
      rewardId: string;
      qrToken: string;
      shardId: string;
      status: string;
    }>("/v1/vendor/merchants", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  createProgramme: (body: Record<string, unknown>) =>
    request<{ programmeId: string; ruleId: string; status: string; idempotent: boolean }>("/v1/vendor/programmes", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  createReward: (body: Record<string, unknown>) =>
    request<{ rewardId: string; merchantId: string; programmeId: string; status: string; idempotent: boolean }>("/v1/vendor/rewards", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  uploadLogo: (body: FormData) => uploadRequest<{ merchantId: string; objectKey: string; logoUrl: string }>("/v1/vendor/assets/logo", body),
  createCampaign: (body: Record<string, unknown>) =>
    request<{ campaignId: string; queued: boolean }>("/v1/vendor/campaigns", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  staffUsers: () => request<{ staff: MerchantStaffUser[] }>("/v1/vendor/staff"),
  inviteStaff: (body: Record<string, unknown>) =>
    request<{ staffUserId: string; merchantId: string; email: string; role: string; status: string }>("/v1/vendor/staff", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  updateStaffStatus: (staffUserId: string, status: "active" | "disabled") =>
    request<{ staffUserId: string; merchantId: string; status: string }>(`/v1/vendor/staff/${staffUserId}/status`, {
      method: "POST",
      body: JSON.stringify({ status })
    }),
  requestExport: (body: Record<string, unknown>) =>
    request<{ exportId: string; merchantId: string; status: string }>("/v1/vendor/exports", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  exports: () => request<{ exports: ExportJobSummary[] }>("/v1/vendor/exports"),
  exportDownloadUrl: (exportId: string) => `${API_BASE}/v1/vendor/exports/${exportId}/download`,
  staffLookup: (query: string) => request<{ result: unknown }>(`/v1/staff/lookup?q=${encodeURIComponent(query)}`),
  addPoints: (body: Record<string, unknown>) => {
    requireOnline("add points");
    return request<{ ledgerId: string; balance: number }>("/v1/staff/points", {
      method: "POST",
      body: JSON.stringify(body)
    });
  },
  confirmRedemption: (body: Record<string, unknown>) => {
    requireOnline("redeem rewards");
    return request<{ redemptionId: string; ledgerId: string; balance: number; status: string }>("/v1/staff/redemptions/confirm", {
      method: "POST",
      body: JSON.stringify(body)
    });
  },
  recentActions: () => request<Array<Record<string, unknown>>>("/v1/staff/recent-actions")
};

export function idempotencyKey(scope: string) {
  return `${scope}:${crypto.randomUUID()}`;
}
